import { useState } from "react";
import { X, Copy, Check, Share2 } from "lucide-react";
import { Link } from "react-router";
import { useAuthStore } from "../store/authStore";
import { routes } from "../routes";

interface ReferralModalProps {
  open: boolean;
  onClose: () => void;
}

export function ReferralModal({ open, onClose }: ReferralModalProps) {
  const [copied, setCopied] = useState(false);
  const user = useAuthStore((s) => s.user);
  const tokens = useAuthStore((s) => s.tokens);
  const isLoggedIn = !!tokens?.access?.token;

  if (!open) return null;

  const referralLink = isLoggedIn && user
    ? `${window.location.origin}/?ref=${user.id}`
    : "";

  const shareText =
    "Turn your photos into art — and we both get 10 free creations!";

  async function copyLink() {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for restricted browsers
      const textarea = document.createElement("textarea");
      textarea.value = referralLink;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  async function shareLink() {
    if (!referralLink) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Ghibgen", text: shareText, url: referralLink });
      } catch {
        // User cancelled share or share failed — fall back to copy
        await copyLink();
      }
    } else {
      await copyLink();
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-slate-400 hover:text-slate-600"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-lg font-semibold text-night-ink">
          Invite a friend
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Share your link. You and your friend each get{" "}
          <span className="font-semibold text-primary-400">10 free creations</span>{" "}
          when they sign up!
        </p>

        {isLoggedIn && user ? (
          <>
            <div className="mt-4 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm text-night-ink">
                {referralLink}
              </span>
              <button
                type="button"
                onClick={copyLink}
                className="ml-2 shrink-0 text-primary-400 hover:text-primary-500"
                aria-label="Copy link"
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>

            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={copyLink}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-night-ink transition-colors hover:bg-slate-50"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied!" : "Copy link"}
              </button>
              <button
                type="button"
                onClick={shareLink}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-400 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-500"
              >
                <Share2 className="h-4 w-4" />
                Share
              </button>
            </div>
          </>
        ) : (
          <div className="mt-4">
            <p className="text-sm text-slate-500">
              Log in to get your referral link.
            </p>
            <Link
              to={routes.login}
              onClick={onClose}
              className="mt-3 inline-block rounded-lg bg-primary-400 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-500"
            >
              Log in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
